import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { LLink as Link } from '../components/LocalizedLink';
import { KeyRound, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  // eslint-disable-next-line no-empty-pattern
  const {} = useAuth(); // placeholder in case we later want to hydrate

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      const res = await authApi.resetPassword(token, password);
      if (res?.token) localStorage.setItem('esi_token', res.token);
      setDone(true);
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (err.message || 'Reset failed'));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-72px)] bg-[#f7fafd] py-16">
      <div className="container-x max-w-md mx-auto">
        <div className="flex flex-col items-center mb-6">
          <span className="w-12 h-12 rounded-full bg-[#0d3a8a] text-white inline-flex items-center justify-center">
            <KeyRound size={20} />
          </span>
          <h1 className="mt-4 text-2xl font-bold text-[#0b1f3a]">Choose a new password</h1>
          <p className="text-slate-600 text-sm mt-1 text-center">
            Pick a password with at least 6 characters. You&apos;ll be signed in automatically.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-7 shadow-sm">
          {done ? (
            <div className="text-center py-4" data-testid="reset-done">
              <span className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center justify-center mb-3">
                <CheckCircle2 size={22} />
              </span>
              <h3 className="text-lg font-semibold text-[#0b1f3a]">Password updated</h3>
              <p className="text-slate-600 text-sm mt-2">Redirecting you to your dashboard...</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
              {error && (
                <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 text-rose-700 rounded-lg px-3 py-2 text-sm">
                  <AlertCircle size={16} className="mt-0.5" /> {error}
                </div>
              )}
              <div>
                <Label className="field-label">New password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  autoFocus
                  data-testid="reset-password"
                />
              </div>
              <div>
                <Label className="field-label">Confirm new password</Label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  data-testid="reset-password-confirm"
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                className="btn-primary w-full justify-center disabled:opacity-60"
                data-testid="reset-submit"
              >
                {busy ? 'Saving...' : 'Set new password'}
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          <Link to="/login" className="text-[#0d3a8a] font-semibold hover:underline" data-testid="reset-back-login">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
